import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useAction, useQuery } from "convex/react";
import { BookOpenIcon, PlusIcon } from "lucide-react";
import { createParser, parseAsBoolean, useQueryState } from "nuqs";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import {
	Field,
	FieldDescription,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import SkillEditor from "./SkillEditor";

const CREATE_DRAFT_STORAGE_KEY = "clerical.staff.skills.new-draft.v1";
const DEFAULT_SOURCE =
	"# Skill\n\n## Step 1 — First action\n\nDescribe the action.";
const EMPTY_CREATE_FORM = { name: "", source: DEFAULT_SOURCE };
const skillIdParser = createParser<Id<"clericalSkills">>({
	parse: (value) => (value.trim() ? (value as Id<"clericalSkills">) : null),
	serialize: (value) => value,
});
const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
	dateStyle: "medium",
	timeStyle: "short",
});

type SkillRow = {
	_id: Id<"clericalSkills">;
	slug: string;
	name: string;
	description: string;
	status: "active" | "archived";
	sha256: string;
	size: number;
	invocationCount: number;
	createdByUserId: Id<"users">;
	updatedByUserId: Id<"users">;
	createdAt: number;
	updatedAt: number;
};

type CreateForm = typeof EMPTY_CREATE_FORM;

function readCreateDraft(): CreateForm {
	if (typeof window === "undefined") return EMPTY_CREATE_FORM;
	try {
		const raw = window.localStorage.getItem(CREATE_DRAFT_STORAGE_KEY);
		if (!raw) return EMPTY_CREATE_FORM;
		const parsed: unknown = JSON.parse(raw);
		if (
			!parsed ||
			typeof parsed !== "object" ||
			!("version" in parsed) ||
			parsed.version !== 1 ||
			!("name" in parsed) ||
			typeof parsed.name !== "string" ||
			!("source" in parsed) ||
			typeof parsed.source !== "string"
		) {
			return EMPTY_CREATE_FORM;
		}
		return { name: parsed.name, source: parsed.source };
	} catch {
		return EMPTY_CREATE_FORM;
	}
}

function writeCreateDraft(form: CreateForm) {
	try {
		window.localStorage.setItem(
			CREATE_DRAFT_STORAGE_KEY,
			JSON.stringify({ version: 1, ...form }),
		);
	} catch {
		return;
	}
}

function clearCreateDraft() {
	try {
		window.localStorage.removeItem(CREATE_DRAFT_STORAGE_KEY);
	} catch {
		return;
	}
}

function StatusBadge({ status }: { status: SkillRow["status"] }) {
	return (
		<Badge variant={status === "archived" ? "outline" : "secondary"}>
			{status === "archived" ? "Archived" : "Active"}
		</Badge>
	);
}

const skillColumns: ColumnDef<SkillRow>[] = [
	{
		accessorKey: "name",
		header: "Skill",
		cell: ({ row }) => (
			<div className="flex min-w-0 flex-col gap-0.5">
				<span className="truncate font-medium">{row.original.name}</span>
				<span className="truncate text-xs text-muted-foreground">
					{row.original.description}
				</span>
			</div>
		),
	},
	{
		accessorKey: "slug",
		header: "Reference",
		cell: ({ row }) => (
			<span className="font-mono text-xs">skill://{row.original.slug}</span>
		),
	},
	{
		accessorKey: "status",
		header: "Status",
		cell: ({ row }) => <StatusBadge status={row.original.status} />,
	},
	{
		accessorKey: "invocationCount",
		header: "Loads",
		cell: ({ row }) => row.original.invocationCount.toLocaleString(),
	},
	{
		accessorKey: "updatedAt",
		header: "Updated",
		cell: ({ row }) => dateTimeFormatter.format(row.original.updatedAt),
	},
];

export default function SkillsPage() {
	const skills = useQuery(api.agent.skills.library.listSkillsForStaff, {});
	const [createOpen, setCreateOpen] = useQueryState(
		"newSkill",
		parseAsBoolean.withDefault(false),
	);
	const [selectedSkillId, setSelectedSkillId] = useQueryState(
		"skill",
		skillIdParser,
	);
	const data = skills ?? [];
	const table = useReactTable({
		data,
		columns: skillColumns,
		getCoreRowModel: getCoreRowModel(),
	});

	function changeCreateOpen(open: boolean) {
		void setCreateOpen(open ? true : null);
	}

	function openSkill(skillId: Id<"clericalSkills">) {
		void setSelectedSkillId(skillId);
	}

	function closeSkillSheet(open: boolean) {
		if (!open) void setSelectedSkillId(null);
	}

	return (
		<div className="flex h-full min-h-0 flex-col bg-background">
			<header className="flex flex-col gap-3 border-b px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:pl-14 sm:pr-6">
				<div className="flex flex-col gap-1">
					<h1 className="text-lg font-semibold">Skills</h1>
					<p className="text-xs text-muted-foreground">
						Manage reusable agent procedures and review runtime usage.
					</p>
				</div>
				<CreateSkillDialog
					open={createOpen}
					onOpenChange={changeCreateOpen}
					onCreated={openSkill}
				/>
			</header>

			<main className="flex min-h-0 flex-1 flex-col">
				<ScrollArea className="min-h-0 flex-1">
					{skills === undefined ? (
						<div className="flex flex-col gap-3 p-4">
							<Skeleton className="h-12 w-full" />
							<Skeleton className="h-12 w-full" />
							<Skeleton className="h-12 w-full" />
						</div>
					) : skills.length === 0 ? (
						<Empty className="min-h-96 border-0">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<BookOpenIcon />
								</EmptyMedia>
								<EmptyTitle>No skills yet</EmptyTitle>
								<EmptyDescription>
									Create a Markdown skill for Clerical to load when needed.
								</EmptyDescription>
							</EmptyHeader>
							<EmptyContent>
								<Button onClick={() => changeCreateOpen(true)}>
									Create skill
								</Button>
							</EmptyContent>
						</Empty>
					) : (
						<Table>
							<TableHeader>
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<TableHead key={header.id}>
												{header.isPlaceholder
													? null
													: flexRender(
															header.column.columnDef.header,
															header.getContext(),
														)}
											</TableHead>
										))}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										tabIndex={0}
										aria-label={`Edit ${row.original.name}`}
										data-state={
											selectedSkillId === row.original._id
												? "selected"
												: undefined
										}
										className="cursor-pointer"
										onClick={() => openSkill(row.original._id)}
										onKeyDown={(event) => {
											if (event.key !== "Enter" && event.key !== " ") return;
											event.preventDefault();
											openSkill(row.original._id);
										}}
									>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</TableCell>
										))}
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</ScrollArea>
			</main>

			<Sheet open={selectedSkillId !== null} onOpenChange={closeSkillSheet}>
				<SheetContent className="data-[side=right]:w-full data-[side=right]:max-w-none data-[side=right]:sm:w-[80vw] data-[side=right]:sm:max-w-[80vw]">
					{selectedSkillId ? (
						<SkillEditor
							key={selectedSkillId}
							skillId={selectedSkillId}
							onDeleted={() => void setSelectedSkillId(null)}
						/>
					) : null}
				</SheetContent>
			</Sheet>
		</div>
	);
}

function CreateSkillDialog({
	open,
	onOpenChange,
	onCreated,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreated: (skillId: Id<"clericalSkills">) => void;
}) {
	const saveSkill = useAction(api.agent.skills.actions.saveSkill);
	const [form, setForm] = useState<CreateForm>(() => readCreateDraft());
	const [saving, setSaving] = useState(false);

	function updateForm(patch: Partial<CreateForm>) {
		const next = { ...form, ...patch };
		setForm(next);
		writeCreateDraft(next);
	}

	const canSave = useMemo(
		() => form.name.trim().length > 0 && form.source.trim().length > 0,
		[form.name, form.source],
	);

	async function createSkill() {
		if (!canSave) return;
		setSaving(true);
		try {
			const result = await saveSkill({
				name: form.name,
				source: form.source,
			});
			clearCreateDraft();
			setForm(EMPTY_CREATE_FORM);
			toast.success("Skill created");
			onCreated(result.skillId);
			onOpenChange(false);
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Could not create skill",
			);
		} finally {
			setSaving(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger render={<Button />}>
				<PlusIcon data-icon="inline-start" />
				New skill
			</DialogTrigger>
			<DialogContent className="sm:max-w-3xl">
				<ScrollArea className="max-h-[calc(100vh-4rem)]">
					<div className="flex flex-col gap-4">
						<DialogHeader>
							<DialogTitle>Create skill</DialogTitle>
							<DialogDescription>
								Reference and routing description generate automatically when
								saved.
							</DialogDescription>
						</DialogHeader>
						<FieldGroup>
							<Field>
								<FieldLabel htmlFor="new-skill-name">Name</FieldLabel>
								<Input
									id="new-skill-name"
									value={form.name}
									onChange={(event) => updateForm({ name: event.target.value })}
									placeholder="Document cloning workflow"
									maxLength={120}
								/>
								<FieldDescription>
									Skill reference derives from this name.
								</FieldDescription>
							</Field>
							<Field>
								<FieldLabel id="new-skill-source-label">
									Instructions
								</FieldLabel>
								<MarkdownEditor
									ariaLabelledBy="new-skill-source-label"
									value={form.source}
									onChange={(source) => updateForm({ source })}
									placeholder="Write operational steps..."
								/>
								<FieldDescription>
									Changes stay in this browser until skill saves.
								</FieldDescription>
							</Field>
						</FieldGroup>
						<DialogFooter>
							<Button variant="outline" onClick={() => onOpenChange(false)}>
								Cancel
							</Button>
							<Button onClick={createSkill} disabled={!canSave || saving}>
								{saving ? <Spinner data-icon="inline-start" /> : null}
								Create skill
							</Button>
						</DialogFooter>
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
}
